import { useEffect, useState } from "react";
import { gitlabApiPrefix } from "../components/SupabaseClient";

function getHeader(headers, key) {
  return headers.get(key) || headers.get(key.toLowerCase());
}

export const useGitLabFileMetadata = (projectId, filePath, ref = "HEAD") => {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId || !filePath) {
      setMetadata(null);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    const encodedPath = encodeURIComponent(filePath);

    const loadMetadata = async () => {
      setLoading(true);
      setError(null);

      try {
        const headResp = await fetch(
          `${gitlabApiPrefix}/api/v4/projects/${projectId}/repository/files/${encodedPath}?ref=${encodeURIComponent(ref)}`,
          {
            method: "HEAD",
            signal,
          },
        );

        if (!headResp.ok) {
          throw new Error(
            `GitLab API error: ${headResp.status} ${headResp.statusText}`,
          );
        }

        const fileName =
          getHeader(headResp.headers, "x-gitlab-file-name") ||
          filePath.split("/").pop() ||
          filePath;

        const sizeHeader = getHeader(headResp.headers, "x-gitlab-size");

        const lastCommitId = getHeader(
          headResp.headers,
          "x-gitlab-last-commit-id",
        );

        let lastCommitMessage = "";
        if (lastCommitId) {
          const commitParams = new URLSearchParams({
            path: filePath,
            per_page: "1",
            ref_name: ref,
          });
          const commitResp = await fetch(
            `${gitlabApiPrefix}/api/v4/projects/${projectId}/repository/commits?${commitParams}`,
            { signal },
          );

          if (commitResp.ok && !signal.aborted) {
            const commits = await commitResp.json();
            lastCommitMessage =
              commits[0]?.title || commits[0]?.message?.split("\n")[0] || "";
          }
        }

        if (signal.aborted) return;

        setMetadata({
          fileName,
          filePath,
          size: sizeHeader ? Number(sizeHeader) : null,
          blobId: getHeader(headResp.headers, "x-gitlab-blob-id"),
          contentSha256: getHeader(headResp.headers, "x-gitlab-content-sha256"),
          lastCommitId,
          lastCommitMessage,
          executeFilemode:
            getHeader(headResp.headers, "x-gitlab-execute-filemode") === "true",
          ref,
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[useGitLabFileMetadata] Failed to fetch metadata:", err);
        setMetadata(null);
        setError(err.message);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadMetadata();

    return () => controller.abort();
  }, [filePath, projectId, ref]);

  return { metadata, loading, error };
};
